import type { MappingTarget } from '@fundline/shared/imports/mapping';
import {
  intakeAttestation,
  type IntakeSourceRequest,
  PERSON_CONSENT_SOURCE_LABELS,
  PERSON_CONSENT_SOURCES,
  type PersonConsentSource,
} from '@fundline/shared/schemas/imports';
import { type FormEvent, useMemo, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { env } from '../../lib/env.ts';
import { targetOptions } from '../imports/import-form.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { SettingsTabs } from '../settings/SettingsTabs.tsx';
import { useFieldDefinitionsQuery } from '../settings/use-fields.ts';
import { useTagsQuery } from '../settings/use-tags.ts';
import { useWorkspaceQuery } from '../settings/use-workspace.ts';
import {
  type IntakeEvent,
  type IntakeSource,
  useIntakeEventsQuery,
  useIntakeMutations,
  useIntakeSourcesQuery,
} from './use-intake.ts';

/** Where vendors and web forms send leads (the worker's public endpoint). */
export function intakeEndpoint(): string {
  const base = env().VITE_WORKER_URL || window.location.origin;
  return `${base}/intake/v1/leads`;
}

const EVENT_STATUS: Record<
  IntakeEvent['status'],
  { label: string; role: 'neutral' | 'pending' | 'funded' | 'declined' }
> = {
  received: { label: 'Waiting', role: 'pending' },
  processed: { label: 'Added', role: 'funded' },
  rejected: { label: 'Not added', role: 'neutral' },
  failed: { label: 'Failed', role: 'declined' },
};

const OUTCOME_WORDS: Record<string, string> = {
  created: 'New merchant',
  updated: 'Existing merchant filled in',
  invalid_phone: 'Invalid phone number',
  outside_us: 'Outside the US',
  no_consent: 'No consent time',
  suppressed: 'On the suppression list',
  do_not_contact: 'Marked do not contact',
  archived: 'Archived merchant',
  landline: 'Landline',
  invalid_lead: 'Unreadable lead',
  no_assignee: 'Nobody to assign it to',
  error: 'Error',
};

interface SourceForm {
  name: string;
  assignmentMode: 'single' | 'round_robin';
  assignee: string;
  roundRobin: string[];
  tagIds: string[];
  consentSource: PersonConsentSource | '';
  consentScope: string;
  requireConsentTimestamp: boolean;
  attested: boolean;
  lookupEnabled: boolean;
  mapping: Array<{ key: string; target: MappingTarget | '' }>;
}

function emptyForm(userId: string): SourceForm {
  return {
    name: '',
    assignmentMode: 'single',
    assignee: userId,
    roundRobin: [],
    tagIds: [],
    consentSource: '',
    consentScope: '',
    requireConsentTimestamp: true,
    attested: false,
    lookupEnabled: false,
    mapping: [],
  };
}

function formFrom(s: IntakeSource, userId: string): SourceForm {
  return {
    name: s.name,
    assignmentMode: s.assignmentMode,
    assignee: s.assigneeUserId ?? userId,
    roundRobin: s.roundRobinUserIds,
    tagIds: s.defaultTagIds,
    consentSource: PERSON_CONSENT_SOURCES.find((c) => c === s.consentSource) ?? '',
    consentScope: s.consentScope ?? '',
    requireConsentTimestamp: s.requireConsentTimestamp,
    attested: !s.requireConsentTimestamp,
    lookupEnabled: s.lookupEnabled,
    mapping: Object.entries(s.mapping).map(([key, target]) => ({
      key,
      target: target as MappingTarget,
    })),
  };
}

function toRequest(f: SourceForm): IntakeSourceRequest | string {
  if (!f.name.trim()) return 'Name the source';
  if (!f.consentSource) return 'Choose where consent comes from';
  if (!f.consentScope.trim()) return 'Say what the consent covers';
  if (!f.requireConsentTimestamp && !f.attested) {
    return 'Confirm that every lead agreed to texts before turning off the consent time';
  }
  if (f.assignmentMode === 'round_robin' && f.roundRobin.length === 0) {
    return 'Choose at least one person for round robin';
  }
  const mapping: Record<string, MappingTarget> = {};
  for (const m of f.mapping)
    if (m.key.trim() && m.target) mapping[m.key.trim().toLowerCase()] = m.target;
  return {
    name: f.name.trim(),
    mapping,
    assignment:
      f.assignmentMode === 'single'
        ? { mode: 'single', userId: f.assignee }
        : { mode: 'round_robin', userIds: f.roundRobin },
    defaultTagIds: f.tagIds,
    consentSource: f.consentSource,
    consentScope: f.consentScope.trim(),
    requireConsentTimestamp: f.requireConsentTimestamp,
    attested: f.attested,
    lookupEnabled: f.lookupEnabled,
  };
}

/** Settings → Lead sources (M8, admins). */
export function LeadSourcesPage() {
  const { workspaceId, userId } = useWorkspaceClaims();
  const sources = useIntakeSourcesQuery(workspaceId);
  const { create, update, action } = useIntakeMutations(workspaceId);
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null);
  const [editing, setEditing] = useState<IntakeSource | 'new' | null>(null);
  const webhooks = (sources.data ?? []).filter((s) => s.kind === 'webhook');
  const vendors = (sources.data ?? []).filter((s) => s.kind === 'csv');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Lead sources: web forms and vendors that send leads, and the columns saved for vendor files."
      />
      <SettingsTabs />

      {newKey ? (
        <Alert tone="success">
          <span className="flex flex-col gap-2">
            <span>API key for {newKey.name}. Copy it now: it is shown only once.</span>
            <code
              className="break-all rounded-md bg-surface px-2 py-1 footnote"
              data-testid="api-key"
            >
              {newKey.key}
            </code>
            <span className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void navigator.clipboard?.writeText(newKey.key)}
              >
                Copy key
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNewKey(null)}>
                Done
              </Button>
            </span>
          </span>
        </Alert>
      ) : null}

      <Card title="How to send leads">
        <div className="flex flex-col gap-2 callout text-ink">
          <p>
            POST one lead as JSON to <code className="break-all">{intakeEndpoint()}</code> with the
            header <code>Authorization: Bearer &lt;key&gt;</code>. The answer is 202 as soon as the
            lead is saved; it never says whether a number is known or blocked.
          </p>
          <pre className="whitespace-pre-wrap break-all rounded-md bg-surface p-3 footnote text-ink">
            {`{"phone": "(212) 555-0101", "business_name": "Acme Pizza", "state": "NY",
 "email": "owner@example.com", "consent_at": "2026-10-13T14:03:00Z",
 "ip_address": "203.0.113.7", "page_url": "https://example.com/apply"}`}
          </pre>
          <p className="text-ink-muted">
            Keys match field names and common names (phone, mobile, company, opt_in_date…). Add your
            own below. Nothing is texted when a lead arrives.
          </p>
        </div>
      </Card>

      {editing ? (
        <SourceEditor
          key={editing === 'new' ? 'new' : editing.id}
          initial={editing === 'new' ? emptyForm(userId) : formFrom(editing, userId)}
          busy={create.isPending || update.isPending}
          error={(editing === 'new' ? create.error : update.error)?.message ?? null}
          onCancel={() => setEditing(null)}
          onSave={(body) => {
            if (editing === 'new') {
              create.mutate(body, {
                onSuccess: (r) => {
                  setEditing(null);
                  if (r.apiKey) setNewKey({ name: body.name, key: r.apiKey });
                },
              });
            } else {
              update.mutate({ id: editing.id, body }, { onSuccess: () => setEditing(null) });
            }
          }}
        />
      ) : (
        <div>
          <Button onClick={() => setEditing('new')}>New webhook source</Button>
        </div>
      )}

      {action.isError ? <Alert tone="error">{action.error.message}</Alert> : null}
      <section className="flex flex-col gap-3" aria-label="Webhook sources">
        <h2 className="body font-semibold text-ink">Webhook sources</h2>
        {sources.isPending ? <Spinner label="Loading lead sources" /> : null}
        {sources.isError ? <Alert tone="error">{sources.error.message}</Alert> : null}
        {sources.data && webhooks.length === 0 ? (
          <EmptyState title="No webhook sources yet">
            Add one for each form or vendor that sends leads.
          </EmptyState>
        ) : null}
        {webhooks.map((s) => (
          <WebhookSourceCard
            key={s.id}
            source={s}
            onEdit={() => setEditing(s)}
            onAction={(act) =>
              action.mutate(
                { id: s.id, act },
                {
                  onSuccess: (r) =>
                    r.apiKey ? setNewKey({ name: s.name, key: r.apiKey }) : undefined,
                },
              )
            }
          />
        ))}
      </section>

      <section className="flex flex-col gap-3" aria-label="Saved vendor columns">
        <h2 className="body font-semibold text-ink">Saved vendor columns</h2>
        {sources.data && vendors.length === 0 ? (
          <p className="callout text-ink-muted">
            Saved from the import screen when you name a vendor.
          </p>
        ) : null}
        {vendors.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {vendors.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium text-ink">{v.name}</span>
                  <span className="footnote text-ink-muted">
                    {Object.keys(v.mapping).length} columns saved
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => action.mutate({ id: v.id, act: 'archive' })}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

function SourceEditor({
  initial,
  busy,
  error,
  onCancel,
  onSave,
}: {
  initial: SourceForm;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (body: IntakeSourceRequest) => void;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const members = useMembersQuery(workspaceId);
  const tags = useTagsQuery(workspaceId);
  const fields = useFieldDefinitionsQuery(workspaceId);
  const workspace = useWorkspaceQuery(workspaceId);
  const [form, setForm] = useState(initial);
  const [problem, setProblem] = useState<string | null>(null);
  const active = (members.data ?? []).filter((m) => m.status === 'active');
  const options = useMemo(() => targetOptions(fields.data ?? []), [fields.data]);
  const set = <K extends keyof SourceForm>(key: K, value: SourceForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  function submit(e: FormEvent) {
    e.preventDefault();
    const r = toRequest(form);
    if (typeof r === 'string') {
      setProblem(r);
      return;
    }
    setProblem(null);
    onSave(r);
  }

  return (
    <Card title={initial.name ? `Edit ${initial.name}` : 'New webhook source'}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          maxLength={80}
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="callout font-semibold text-ink">New leads go to</legend>
          {(['single', 'round_robin'] as const).map((mode) => (
            <label key={mode} className="flex items-center gap-2 callout text-ink">
              <input
                type="radio"
                name="source-assignment"
                className="size-4"
                checked={form.assignmentMode === mode}
                onChange={() => set('assignmentMode', mode)}
              />
              {mode === 'single' ? 'One person' : 'Round robin'}
            </label>
          ))}
        </fieldset>
        {form.assignmentMode === 'single' ? (
          <SelectField
            label="Person"
            value={form.assignee}
            onChange={(e) => set('assignee', e.target.value)}
            options={active.map((m) => ({
              value: m.userId,
              label: `${m.fullName || m.email} (${m.role})`,
            }))}
          />
        ) : (
          <fieldset className="flex flex-col gap-2">
            <legend className="callout font-semibold text-ink">People</legend>
            {active.map((m) => (
              <label key={m.userId} className="flex items-center gap-2 callout text-ink">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={form.roundRobin.includes(m.userId)}
                  onChange={(e) =>
                    set(
                      'roundRobin',
                      e.target.checked
                        ? [...form.roundRobin, m.userId]
                        : form.roundRobin.filter((x) => x !== m.userId),
                    )
                  }
                />
                {m.fullName || m.email}
              </label>
            ))}
          </fieldset>
        )}
        {tags.data && tags.data.length > 0 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="callout font-semibold text-ink">Tags</legend>
            {tags.data.map((t) => (
              <label key={t.id} className="flex items-center gap-2 callout text-ink">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={form.tagIds.includes(t.id)}
                  onChange={(e) =>
                    set(
                      'tagIds',
                      e.target.checked
                        ? [...form.tagIds, t.id]
                        : form.tagIds.filter((x) => x !== t.id),
                    )
                  }
                />
                {t.name}
              </label>
            ))}
          </fieldset>
        ) : null}
        <SelectField
          label="Where does consent come from?"
          value={form.consentSource}
          onChange={(e) =>
            set('consentSource', PERSON_CONSENT_SOURCES.find((c) => c === e.target.value) ?? '')
          }
          options={[
            { value: '', label: 'Choose one' },
            ...PERSON_CONSENT_SOURCES.map((c) => ({
              value: c,
              label: PERSON_CONSENT_SOURCE_LABELS[c],
            })),
          ]}
          data-testid="source-consent-source"
        />
        <TextField
          label="What leads agree to"
          placeholder="Texts about their funding application"
          value={form.consentScope}
          onChange={(e) => set('consentScope', e.target.value)}
          maxLength={500}
        />
        <label className="flex items-start gap-2 callout text-ink">
          <input
            type="checkbox"
            className="mt-0.5 size-4"
            checked={form.requireConsentTimestamp}
            onChange={(e) => set('requireConsentTimestamp', e.target.checked)}
          />
          Every lead must include its consent time (consent_at). Leads without one are not added.
        </label>
        {!form.requireConsentTimestamp ? (
          <label className="flex items-start gap-2 callout text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={form.attested}
              onChange={(e) => set('attested', e.target.checked)}
            />
            {intakeAttestation(workspace.data?.name ?? 'this workspace')}
          </label>
        ) : null}
        <label className="flex items-start gap-2 callout text-ink">
          <input
            type="checkbox"
            className="mt-0.5 size-4"
            checked={form.lookupEnabled}
            onChange={(e) => set('lookupEnabled', e.target.checked)}
          />
          Look up new numbers and turn away landlines (paid, about $0.004 per new lead)
        </label>
        <fieldset className="flex flex-col gap-2">
          <legend className="callout font-semibold text-ink">Extra keys (optional)</legend>
          {form.mapping.map((m, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-2">
              <TextField
                label="Key in the lead"
                value={m.key}
                onChange={(e) =>
                  set(
                    'mapping',
                    form.mapping.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)),
                  )
                }
              />
              <SelectField
                label="Goes to"
                value={m.target}
                onChange={(e) =>
                  set(
                    'mapping',
                    form.mapping.map((x, j) =>
                      j === i
                        ? {
                            ...x,
                            target: options.find((o) => o.value === e.target.value)?.value ?? '',
                          }
                        : x,
                    ),
                  )
                }
                options={options}
              />
            </div>
          ))}
          <div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => set('mapping', [...form.mapping, { key: '', target: '' }])}
            >
              Add a key
            </Button>
          </div>
        </fieldset>
        {problem ? <Alert tone="error">{problem}</Alert> : null}
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" busy={busy}>
            Save
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

function WebhookSourceCard({
  source: s,
  onEdit,
  onAction,
}: {
  source: IntakeSource;
  onEdit: () => void;
  onAction: (act: 'rotate-key' | 'revoke-key' | 'archive') => void;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const events = useIntakeEventsQuery(workspaceId, s.id);
  const [confirm, setConfirm] = useState<'revoke-key' | 'archive' | null>(null);
  return (
    <Card>
      <div className="flex flex-col gap-3" data-testid="webhook-source">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span className="flex min-w-0 flex-col">
            <span className="headline text-ink">{s.name}</span>
            <span className="footnote text-ink-muted">
              {s.keyHint ? `Key ending ${s.keyHint}` : 'No key: leads are refused'} ·{' '}
              {PERSON_CONSENT_SOURCE_LABELS[s.consentSource as PersonConsentSource] ?? 'Consent'} ·{' '}
              {s.requireConsentTimestamp ? 'consent time required' : 'consent time not required'}
              {s.lookupEnabled ? ' · carrier lookup on' : ''}
            </span>
          </span>
          <span className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={onEdit}>
              Edit
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onAction('rotate-key')}>
              {s.keyHint ? 'New key' : 'Create key'}
            </Button>
            {s.keyHint ? (
              <Button size="sm" variant="ghost" onClick={() => setConfirm('revoke-key')}>
                Turn off key
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setConfirm('archive')}>
              Remove
            </Button>
          </span>
        </div>
        {confirm ? (
          <Alert tone="warning">
            <span className="flex flex-col gap-2">
              {confirm === 'archive'
                ? `Remove ${s.name}? Its key stops working. Leads already added stay.`
                : 'Turn off this key? Leads sent with it are refused until you create a new one.'}
              <span className="flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    onAction(confirm);
                    setConfirm(null);
                  }}
                >
                  {confirm === 'archive' ? 'Remove' : 'Turn off'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                  Cancel
                </Button>
              </span>
            </span>
          </Alert>
        ) : null}
        <h3 className="callout font-semibold text-ink">Latest leads</h3>
        {events.isPending ? <Spinner label="Loading leads" /> : null}
        {events.isError ? <Alert tone="error">{events.error.message}</Alert> : null}
        {events.data?.length === 0 ? <p className="callout text-ink-muted">No leads yet.</p> : null}
        {events.data && events.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline">
            {events.data.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
                data-testid="intake-event"
              >
                <span className="flex min-w-0 flex-col callout">
                  <span className="text-ink">
                    {OUTCOME_WORDS[e.outcome ?? ''] ?? (e.outcome ? e.outcome : 'Waiting')}
                  </span>
                  {e.error ? (
                    <span className="break-words footnote text-ink-muted">{e.error}</span>
                  ) : null}
                  <span className="footnote text-ink-muted">
                    {new Date(e.receivedAt).toLocaleString()}
                  </span>
                </span>
                <Badge role={EVENT_STATUS[e.status].role}>{EVENT_STATUS[e.status].label}</Badge>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
